import { Component, h } from 'preact'
import { connectHits, connectStateResults } from 'react-instantsearch-dom'
import axios from 'axios'
import TopicListItem from '../topic-list-view/TopicListItem'
import Phrase from '../../libs/translationsUtils/phrase'
import tracker from '../../components/tracker/publish'
import { t } from '../../libs/translationsUtils/translations'
import { ZendeskHit, Hit, SearchResults, AllSearchResults, isZendeskResult } from '../algolia/components'

import ZendeskResults from './ZendeskResults'

const eventTracker = () => {
  tracker.trackEvent('userevent', {
    type: 'Topic Initiated',
    trigger: 'widget',
    source: 'search',
    user_role: window.inSidedData.user.role,
    button_label: t('Forum', 'main.navigation.add_topic')
  })
}

interface State {
  topicsList: Array<Hit | ZendeskHit>,
  disablePaginationCheck: boolean
}

interface Props {
  isGuest: boolean,
  isFederatedSearch: boolean,
  enableLoader: boolean,
  attachmentCdn: string,
  defaultCdn: string,
  communityId: string,
  pageCount: number,
  results: Object,
  loaderCallback: (enableLoader: boolean) => void,
  updatePagination: () => void,
  searchResults: SearchResults,
  allSearchResults: AllSearchResults,
  hits: Array<Hit | ZendeskHit>
}

class SearchResultsContainer extends Component<Props, State> {
  constructor (props: Props) {
    super(props)
    this.state = {
      topicsList: [],
      disablePaginationCheck: false
    }

    this.fetchMetadataForTopics = this.fetchMetadataForTopics.bind(this)
    this.startPagination = this.startPagination.bind(this)
    this.setResultsToDisplay = this.setResultsToDisplay.bind(this)
    this.getZendeskHits = this.getZendeskHits.bind(this)
  }

  communityResults: undefined | SearchResults
  zendeskResults: undefined | SearchResults

  setResultsToDisplay (allSearchResults: AllSearchResults) {
    if (!this.props.isFederatedSearch) {
      return {
        communityResults: this.props.searchResults
      }
    }
    return {
      communityResults: allSearchResults && allSearchResults[this.props.communityId] && allSearchResults[this.props.communityId],
      zendeskResults: allSearchResults && allSearchResults[`${this.props.communityId}-zendesk`] && allSearchResults[`${this.props.communityId}-zendesk`]
    }
  }

  getZendeskHits () {
    return this.zendeskResults ? this.zendeskResults.hits : []
  }

  async fetchMetadataForTopics (topicsIdList: Array<number>) {
    try {
      this.props.loaderCallback(true)
      const result = await axios.get('/search/getTopics', {
        params: {
          topicIds: topicsIdList
        }
      })
      this.props.loaderCallback(false)
      if (result.data) {
        const zendeskHits = this.getZendeskHits()
        this.setState({
          topicsList: this.props.pageCount === 0 ? result.data.concat(zendeskHits) : this.state.topicsList.concat(result.data).concat(zendeskHits),
          disablePaginationCheck: false
        })
      }
    } catch {
      this.props.loaderCallback(false)
    }
  }

  startPagination () {
    this.setState({
      disablePaginationCheck: true
    }, () => this.props.updatePagination())
  }

  componentDidUpdate () {
    const newResults = this.setResultsToDisplay(this.props.allSearchResults)
    const newTopicIds = newResults.communityResults ? newResults.communityResults.hits.map(hit => hit.id) : []
    const ids = this.communityResults ? this.communityResults.hits.map(hit => hit.id) : []
    const shouldUpdateZendesk = JSON.stringify(newResults.zendeskResults) !== JSON.stringify(this.zendeskResults)

    this.zendeskResults = newResults.zendeskResults

    if (JSON.stringify(newTopicIds) !== JSON.stringify(ids)) {
      this.communityResults = newResults.communityResults
      this.zendeskResults = newResults.zendeskResults
      this.fetchMetadataForTopics(newTopicIds)
    } else if (!newTopicIds.length && shouldUpdateZendesk) {
      this.zendeskResults = newResults.zendeskResults
      const zendeskHits = this.getZendeskHits()

      this.setState({
        topicsList: this.props.pageCount === 0 ? zendeskHits : this.state.topicsList.concat(zendeskHits)
      })
    }

    if (!this.state.disablePaginationCheck && this.zendeskResults && (this.zendeskResults.page >= this.zendeskResults.nbPages) && this.communityResults && (this.communityResults.page >= this.communityResults.nbPages)) {
      this.setState({
        disablePaginationCheck: true
      })
    }
  }

  componentDidMount () {
    document.addEventListener('scroll', () => {
      const searchResults = document.getElementsByClassName('thread-list-item')

      if (searchResults.length < 3) {
        return
      }
      const itemToTriggerPagination = searchResults[searchResults.length - 3]
      const itemToTriggerPaginationBoundaries = itemToTriggerPagination.getBoundingClientRect()
      if (window.innerHeight > itemToTriggerPaginationBoundaries.y && !this.state.disablePaginationCheck) {
        this.startPagination()
      }
    })
  }

  render ({ isGuest, hits, searchResults, allSearchResults, attachmentCdn, defaultCdn, enableLoader, isFederatedSearch, pageCount }: Props, { topicsList }: State) {
    let totalTopics = this.communityResults && this.communityResults.nbHits ? this.communityResults.nbHits : 0
    totalTopics = this.zendeskResults && this.zendeskResults.nbHits ? totalTopics + this.zendeskResults.nbHits : totalTopics

    return (
      <div className='col col--main has--side search-topic-list-wrapper'>
        <div className='list__items--topic-count'>{totalTopics} <Phrase p='Topic|Topics' m='Forum' count={{ value: totalTopics.toString() }} /></div>
        {
          totalTopics === 0 ? (
            <div
              className='thread-list-block thread-list-block-empty-status thread-list-block--meta forum-list-view-spacing forum-list-view-spacing--v-s'>
              <p className='p--full-width'>
                <Phrase p='searchresults.none.first' m='Forum' /> <strong>{ searchResults?.query }</strong>.
                <Phrase p='searchresults.none.last' m='Forum' />
              </p>

              <a
                href='/topic/new'
                className='btn--cta btn--new-topic qa-topic-new-button'
                onClick={() => eventTracker()}>
                <Phrase p='main.navigation.add_topic' m='Forum' />
              </a>
            </div>
          ) : (
            <div className='search-topic-list'>
              {
                topicsList.map(topic => {
                  return isZendeskResult(topic) ? (
                    <ZendeskResults hit={topic} />
                  ) : (
                    <TopicListItem
                      key={topic.id}
                      topic={topic}
                      isGuest={isGuest}
                      attachmentCdn={attachmentCdn}
                    />
                  )
                })
              }

            </div>
          )
        }
        {
          enableLoader && pageCount !== 0 ? (
            <div className='instant-search-loader_pagination'>
              <img
                src={`${defaultCdn}/images/spinner.gif`}
                alt='loading...' />
            </div>
          ) : null
        }
      </div>
    )
  }
}

const SearchResultsWithMetadata = connectHits(connectStateResults(SearchResultsContainer))

export default SearchResultsWithMetadata
